import { FlightSegmentBookingStatus, Prisma } from '@prisma/client';

import { _cargos } from '../../../services/cargos';

/**
 * Turkish Cargo (TK) — `listFlightInformation` (LFI) types.
 *
 * Shapes are taken from a live staging response rather than the ICD spreadsheet, which
 * disagrees with the API in a couple of places (the spec spells the dimensions array
 * `dimensionDetaills`; the API expects `dimensionDetails`).
 */
export namespace Turkish {
  /** What `_cargos.ensureWeightPerPiece` hands back — a Cargo with its per-piece weight settled. */
  export type WeightedCargo = ReturnType<typeof _cargos.ensureWeightPerPiece>[number];

  /** TK's APIM scopes credentials per feature set; these are the ones we hold. */
  export type Feature = 'quoting' | 'booking' | 'findBooking' | 'cancel';

  export enum ProductCode {
    SMART = 'SMART',
    PREMIUM = 'PREMIUM',
    URGENT = 'URGENT',
  }

  /**
   * TK identifies the product by the `rateInfo` label on each rate rather than by a code, so these
   * are their labels verbatim — confirmed against a live JFK-FRA response. A combination returns up
   * to three rates: the non-express applicable rate plus the two express products.
   */
  export enum RateInfo {
    ApplicableRate = 'Applicable Rate',
    Premium = 'TK PREMIUM',
    Urgent = 'TK URGENT',
  }

  export const PRODUCTS: Record<ProductCode, { rateInfo: RateInfo; bookingName: string }> = {
    [ProductCode.SMART]: { rateInfo: RateInfo.ApplicableRate, bookingName: 'TK SMART' },
    [ProductCode.PREMIUM]: { rateInfo: RateInfo.Premium, bookingName: 'TK PREMIUM' },
    [ProductCode.URGENT]: { rateInfo: RateInfo.Urgent, bookingName: 'TK URGENT' },
  };

  export enum FlightType {
    PassengerCombination = 'CO',
    Freighter = 'C',
    Truck = 'T',
  }

  export enum Bool {
    Y = 'Y',
    N = 'N',
  }

  /** TK abbreviates both units to a single letter, unlike AC's `KG`/`CM`. */
  export enum WeightUnit {
    KG = 'K',
  }

  export enum DimensionUnit {
    CM = 'C',
  }

  /** `B` is TK's code for cubic metres. */
  export enum VolumeUnit {
    CBM = 'B',
  }

  /** `saveBooking` is one endpoint for three operations; the flag says which. */
  export enum OperationalFlag {
    Insert = 'I',
    Update = 'U',
    Delete = 'D',
  }

  /** Booking status TK answers with, and echoes back on retrieval. */
  export enum BookingStatus {
    Confirmed = 'C',
    Queued = 'Q',
    Cancelled = 'X',
  }

  /**
   * A booked leg's status is TK's booking status, not an IATA segment code (`KK`, `NN`), so it maps
   * straight onto the platform's leg status rather than through the IATA data map.
   */
  export const FLIGHT_SEGMENT_STATUS_BY_BOOKING_STATUS: Record<BookingStatus, FlightSegmentBookingStatus> = {
    [BookingStatus.Confirmed]: FlightSegmentBookingStatus.Confirmed,
    [BookingStatus.Queued]: FlightSegmentBookingStatus.Pending,
    [BookingStatus.Cancelled]: FlightSegmentBookingStatus.Cancelled,
  };

  /** `S`uccess or `F`ailure on the response envelope, independent of HTTP status. */
  export enum ResponseStatus {
    Success = 'S',
    Failure = 'F',
  }

  /** `C` in TK's API samples; `W` appears on bookings their own web portal made. */
  export enum BookingSource {
    Channel = 'C',
    Web = 'W',
  }

  export enum AircraftClassification {
    WideBody = 'Wide Body',
    NarrowBody = 'Narrow Body',
  }

  export enum CheckType {
    Embargo = 'E',
    Restriction = 'R',
  }

  export enum CheckResult {
    Info = 'I',
    Warning = 'W',
  }

  export type AuthResponse = {
    access_token: string;
    expires_in: number;
    token_type: string;
  };

  export type DimensionDetail = {
    length: number;
    width: number;
    height: number;
    numberOfPieces: number;
    /** `length * width * height * numberOfPieces / 1_000_000`. */
    volume: number;
    /** Total weight of `numberOfPieces`, not the per-piece weight. */
    weight: number;
    tiltable: Bool;
    nonStackable: Bool;
    weightUnit: WeightUnit;
    dimensionUnit: DimensionUnit;
  };

  export type CommodityDetail = {
    commodityCode: string;
    /** A single SHC (e.g. `GEN`, `HAZ`), mirroring the filter's `sccDetails`. */
    sccDetails: { sccCode: string };
    pieces: number;
    /** Sum of `dimensionDetails[].weight`. */
    weight: number;
    /** Sum of `dimensionDetails[].volume`. */
    volume: number;
    dimensionDetails: DimensionDetail[];
    uldInfoDetails: never[];
  };

  export type LFI_REQUEST = {
    flightAvailabilityFilterType: {
      origin: string;
      destination: string;
      /** `dd-MMM-yyyy`, upper-cased (e.g. `15-OCT-2024`). */
      shippingDate: string;
      /** TK only accepts `5`. */
      offSetfromShipgDate: 5;
      bookingStation: string;
      agentCode: string;
      /** A single SHC as a bare string, mirroring `commodityDetails[].sccDetails.sccCode`. */
      sccDetails: string;
      commodityDetails: CommodityDetail[];
      productDetails: never[];
    };
  };

  export type OtherCharge = {
    otherChargeHeadCode: string;
    otherChargeHeadName: string;
    /** In the combination's `currencyCode`, not cents. */
    otherChargeAmount: number;
    dueCarrierFlag: boolean;
    dueAgentFlag: boolean;
  };

  /**
   * What a TK price is built from, whether it was quoted or booked. The two differ only in where
   * these come from — an offered `Rate` or a booking's `rateAndCharges` — never in the math.
   */
  export type Pricing = {
    chargeableWeight: number;
    /** In dollars, as TK sends it. */
    weightCharge: number;
    description: string;
    rateInfo: RateInfo;
    origin: string;
    destination: string;
    /** The date the platform's surcharges are priced on — the first leg's departure. */
    validOn: Date;
    /** TK's own named charges. A booking totals them without naming any, so it has none. */
    apiCharges: { label: string; cost: number; comments: string; extra: OtherCharge }[];
  };

  export type Rate = {
    rateInfo: RateInfo;
    /** `<rateQuotationId>&<rate details>`; the quotation id is absent on some rates. */
    rateId: string;
    rateApplied: number;
    chargeableWeight: number;
    weightCharge: number;
    otherChargeDetails?: OtherCharge[];
    grandTotal: number;
    allInRate: Bool;
    IsMCRUsed?: number;
  };

  export type Segment = {
    aircraftType: string;
    aircraftClassification: AircraftClassification;
    combinationId: string;
    combinationSerialNumber: number;
    connectionSerialNumber: number;
    /** `dd-MMM-yyyy HH:mm:ss`, local to `segmentOrigin`. */
    departureDateLeg: string;
    /** `dd-MMM-yyyy HH:mm:ss`, local to `segmentDestination`. */
    arrivalDateLeg: string;
    estimatedDepartureDateLeg: string;
    estimatedArrivalDateLeg: string;
    directFlight: Bool;
    flightCarrierCode: string;
    flightCarrierId: number;
    /** `dd-MMM-yyyy`, local to `segmentOrigin`. */
    flightDate: string;
    flightDateUTC: string;
    flightNumber: string;
    flightSequenceNumber: number;
    flightType: FlightType;
    flightStatus: string;
    isFlightBookable: Bool;
    route: string;
    segmentOrigin: string;
    segmentDestination: string;
    segmentStatus: string;
    stops: number;
    charterFlight: Bool;
    cumulativeJourneyTime?: string;
    flyingHours?: string;
    layoverTime?: string;
    availableFreesaleWeight?: number;
    availableFreesaleVolume?: number;
    segmentUpfrontCheckResults?: {
      segmentUpfrontCheckResult: {
        checkType: CheckType;
        checkResult: CheckResult;
        additionalCheckDetails: string;
        checkDescription: string;
      }[];
    };
  };

  /**
   * The first leg of a bookable flight combination. Subsequent legs hang off
   * `connectionFlights`, and the rates apply to the whole combination.
   */
  export type Combination = Segment & {
    connectionFlights?: Segment[];
    ratingDetails: { rate: Rate[] };
    currencyCode: string;
  };

  /**
   * One rate of one flight combination — the unit a quote variant is drafted from. TK returns a
   * rate per product on every combination, so a combination fans out into several of these.
   */
  export type QuoteRaw = {
    combination: Combination;
    segments: Segment[];
    rate: Rate;
  };

  /** How TK explains a refusal, on every response that can carry one. */
  export type ErrorDetail = { errorCode: string; errorDescription: string };

  /**
   * TK answers 200 either way: `flightSegmentForBookingTypeList` when it has availability, and
   * `errorDetails` (e.g. `Avaliable.Flight.Not.Found`) when it does not.
   */
  export type LFI_RESPONSE = {
    flightSegmentForBookingTypeList?: Combination[];
    errorDetails?: ErrorDetail[];
  };

  /**
   * `saveBooking` request. Modelled on the ICD (`ICD_SaveBooking_WebService.xls`) narrowed to the
   * fields TK's own samples actually send — the ICD describes 188 of them, most for iCargo
   * internals we never populate. `dimensionDetaills` and `uldInfoDetaills` carry TK's typo.
   */
  export type SaveBookingRequest = {
    requestID: string;
    operationalFlag: OperationalFlag;
    bookingDetails: BookingDetails;
  };

  export type BookingDetails = {
    /** TK's booking reference — sent only on an update, to say which booking to replace. */
    ubrNumber?: string;
    shipmentDetails: ShipmentDetails;
    /** TK does not allocate the AWB — it has to be drawn from stock and sent in. */
    shipmentIdentifierDetails: ShipmentIdentifier;
    bookingCommodityDetails: BookingCommodity[];
    bookingFlightDetails: BookingFlight[];
    handlingInformation?: string;
    weightUnit: WeightUnit;
    volumeUnit: VolumeUnit;
    dimensionUnit: DimensionUnit;
    /**
     * The quoted rate TK should price against — the numeric prefix of the LFI `rateId`, which we
     * already keep on the variant as `airline_ref`.
     */
    rateQuotationID?: string | null;
    /**
     * Not an alternative to `rateQuotationID`: TK's own ULD sample sends both, and their spot-rate
     * sample sends only this. Send whichever the variant has.
     */
    spotRate?: number | null;
    createdUser?: string;
    /** Present but always null in TK's samples; declared so the shape is honest. */
    otherChargeDetails?: null;
    dangerousGoodsInformation?: null;
  };

  export type ShipmentDetails = {
    shipmentOrigin: string;
    shipmentDestination: string;
    /** `DD-MMM-YYYY`, origin-local, as everywhere else in TK's API. */
    shippingDate: string;
    bookingStation: string;
    bookingSource: BookingSource;
    /** Pelicargo's IATA agent code, bare digits — the same one quoting sends. */
    agentCode: string;
    agentName: string;
    /** TK's samples set this to the agent code; there is no separate customer account. */
    customerCode: string;
    customerName: string;
    totalNumberOfPieces: number;
    totalWeight: number;
    totalVolume: number;
    currency: string;
    productDetails?: { productName: string };
  };

  export type ShipmentIdentifier = {
    /** The airline's three-digit AWB prefix — `235` for TK. */
    shipmentPrefix: string;
    /** The eight-digit AWB serial drawn from stock. */
    masterDocumentNumber: string;
    ownerCode: string;
    sequenceNumber?: number | null;
    duplicateNumber?: number | null;
    awbStatus?: string | null;
  };

  export type BookingCommodity = {
    commodityCode: string;
    shipmentDescription: string;
    sccCode: string;
    pieces: number;
    weight: number;
    volume: number;
    chargeableWeight: number;
    dimensionDetaills: BookingDimension[];
    grossVolume?: number;
    numberOfUlds?: number;
    uldInfoDetaills?: UldInfo[] | null;
    handlingCodes?: { handlingCode: { code: string }[] };
    /** Weight-bearing special handling, e.g. the dry ice on a PES shipment. */
    complementarySCC?: { sccCode: string; sccWeight: number }[];
  };

  /** One ULD booked against the commodity — `AKE`, `PMC` and friends. */
  export type UldInfo = {
    uldType: string;
    numberOfUlds: number;
    uldWeight?: number;
    uldVolume?: number;
    uldSerialNumber?: string | null;
    commodityCode?: string | null;
    contour?: string | null;
    uldRemarks?: string | null;
  };

  export type BookingDimension = {
    numberOfPieces: number;
    lengthPerPiece: number;
    widthPerPiece: number;
    heightPerpiece: number;
    weight: number;
    tiltable: Bool;
    nonStackable: Bool;
  };

  export type BookingFlight = {
    carrierCode: string;
    flightNumber: string;
    flightDate: string;
    segmentOrigin: string;
    segmentDestination: string;
    segmentDepartureDate: string;
    segmentArrivalDate: string;
    pieces: number;
    weight: number;
    volume: number;
    confirmedWeight?: number;
    confirmedVolume?: number;
    complementarySCCVOs?: null;
  };

  /** TK answers 200 with `status: 'F'` and `errorDetails` on a rejected booking. */
  export type SaveBookingResponse = {
    requestID: string;
    status: ResponseStatus;
    bookingResponseDetails?: BookingResponseDetails;
    errorDetails?: ErrorDetail[];
  };

  export type BookingResponseDetails = {
    /** TK's own identifier for the booking, e.g. `23436506052FRAIST58910`. */
    ubrNumber: string;
    shipmentPrefix?: string;
    masterDocumentNumber?: string;
    bookingStatus?: BookingStatus;
    /** `DD-MMM-YYYY HH:mm:ss.SSS`. */
    lastUpdateTime: string;
    bookingVersion?: number;
    bookingOverview?: BookingOverview;
  };

  /**
   * A TK booking, whichever call returned it. `saveBooking` and `findBooking` describe the same
   * booking in two envelopes; this is what the platform reads off either.
   */
  export type Booking = {
    ubrNumber?: string;
    shipmentPrefix?: string;
    masterDocumentNumber?: string;
    bookingStatus?: BookingStatus;
    bookingVersion?: number;
    origin?: string;
    destination?: string;
    product?: string;
    /** Latest acceptance time, origin-local. Only `saveBooking` sends it in a shape we read. */
    lat?: string;
    legs: BookingLeg[];
    /** Only `saveBooking` prices a booking. */
    rateAndCharges?: BookingOverview['rateAndCharges'];
  };

  /** One booked leg, with times origin- and destination-local as TK sends them. */
  export type BookingLeg = Pick<
    NonNullable<BookingOverview['shipmentDetails']>[number],
    'carrierCode' | 'flightNumber' | 'origin' | 'destination' | 'std' | 'sta' | 'status'
  >;

  /** A leg as the platform records it off a booking; `departs_at` is a real Date so legs can be ordered. */
  export type BookedLeg = Prisma.FlightSegmentCreateManyInput & { departs_at: Date };

  export type BookingOverview = {
    origin: string;
    destination: string;
    /** Latest acceptance time, origin-local. */
    lat?: string;
    product?: string;
    commodity?: string;
    sccCodes?: string;
    bookingDateTime?: string;
    rateAndCharges?: {
      rateApplied: number;
      chargeableWeight: number;
      weightCharge: number;
      otherCharge?: number;
      grandTotal: number;
      currencyCode: string;
    };
    shipmentDetails?: {
      carrierCode: string;
      flightNumber: string;
      flightType?: string;
      origin: string;
      destination: string;
      std: string;
      sta: string;
      pieces: number;
      weight: number;
      volume: number;
      status?: BookingStatus;
    }[];
  };

  /**
   * `findBooking` reads a booking back by its air waybill. Its sibling `findAWB` looks similar but
   * reads AWB data capture instead, and refuses a booking whose waybill has not been captured.
   */
  export type FindBookingRequest = {
    requestID: string;
    bookingDetailsFilter: {
      shipmentIdentifierDetails: Pick<ShipmentIdentifier, 'shipmentPrefix' | 'masterDocumentNumber'>;
    };
  };

  /**
   * Narrowed to what reading a booking back is for. TK returns far more — acceptance flags, LAT in
   * two time zones, split-booking state — none of which the platform records today.
   */
  export type FindBookingResponse = {
    requestID: string;
    status: ResponseStatus;
    bookingDetails?: {
      ubrNumber: string;
      shipmentIdentifierDetails?: ShipmentIdentifier;
      shipmentDetails?: Omit<Partial<ShipmentDetails>, 'productDetails'> & {
        productDetails?: {
          productName: string;
          productCode?: number;
          productPriority?: string;
          productTransportMode?: string;
        };
        isAccepted?: boolean;
        lastUpdateTime?: string;
      };
      bookingCommodityDetails?: BookingCommodity[];
      bookingFlightDetails?: BookingFlight[];
      handlingInformation?: string;
      bookingStatus?: BookingStatus;
      bookingVersion?: number;
      /** TK's own flag for whether it drew the waybill from stock, rather than us sending one. */
      isAWBAutoPopulatedFromStock?: boolean;
      createdUser?: string;
    };
    errorDetails?: ErrorDetail[];
  };

  /** `cancelBooking` wraps its filter in `requestData` — a third envelope convention. */
  export type CancelBookingRequest = {
    requestData: {
      requestID: string;
      bookingDetailsFilter: {
        ubrNumber: string;
        agentCode: string;
        reasonCode: string;
        remarks?: string;
        shipmentIdentifierDetails?: Pick<ShipmentIdentifier, 'shipmentPrefix' | 'masterDocumentNumber'>;
      };
    };
  };

  export type CancelBookingResponse = {
    responseDetails?: {
      requestID: string;
      status: ResponseStatus;
      bookingResponseDetails?: BookingResponseDetails;
      errorDetails?: ErrorDetail[];
    };
  };

  /**
   * Every TK endpoint we call, keyed the way {@link AuthenticatedClient} reads a type map: by method,
   * then by path. TK gives every endpoint its own host, so each feature's client reaches its one
   * through its own gateway host — see `GATEWAY_BY_FEATURE` in `./constants.ts`.
   */
  export type Api = {
    POST: {
      '/listflightinformation': { request: { body: LFI_REQUEST }; response: LFI_RESPONSE };
      '/savebooking': { request: { body: SaveBookingRequest }; response: SaveBookingResponse };
      '/findbooking': { request: { body: FindBookingRequest }; response: FindBookingResponse };
      '/cancelbooking': { request: { body: CancelBookingRequest }; response: CancelBookingResponse };
    };
  };
}
